import React, { useCallback, useRef, useState } from "react";
import { ImagePlus, X, Replace, Trash2, Maximize2 } from "lucide-react";
import { toast } from "sonner";

// Bild im Browser optimieren: max. Kante ~1600px, JPEG ~82%, Seitenverhältnis erhalten.
async function optimizeImage(file, maxDim = 1600, quality = 0.82) {
  const dataUrl = await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = (e) => resolve(e.target.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
  const img = await new Promise((resolve, reject) => {
    const im = new Image();
    im.onload = () => resolve(im);
    im.onerror = reject;
    im.src = dataUrl;
  });
  let { width, height } = img;
  if (Math.max(width, height) > maxDim) {
    const scale = maxDim / Math.max(width, height);
    width = Math.round(width * scale);
    height = Math.round(height * scale);
  }
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff"; // weißer Hintergrund für transparente PNGs
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(img, 0, 0, width, height);
  return canvas.toDataURL("image/jpeg", quality);
}

// Wiederverwendbares Bildfeld: genau EIN Bild. Datei-Upload, Drag&Drop, Clipboard-Paste.
export default function ImageField({ value, onChange, testId = "image-field", accent = "brand" }) {
  const fileRef = useRef();
  const [dragActive, setDragActive] = useState(false);
  const [focused, setFocused] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null); // neues Bild, das ein vorhandenes ersetzen würde
  const [lightbox, setLightbox] = useState(false);

  const processFile = useCallback(async (file) => {
    if (!file || !file.type?.startsWith("image/")) {
      toast.error("Bitte eine Bilddatei auswählen");
      return;
    }
    setBusy(true);
    try {
      const optimized = await optimizeImage(file);
      if (value) {
        setPending(optimized); // Bestätigung anfordern
      } else {
        onChange(optimized);
        toast.success("Bild hinzugefügt");
      }
    } catch {
      toast.error("Bild konnte nicht verarbeitet werden");
    } finally {
      setBusy(false);
    }
  }, [value, onChange]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) processFile(file);
  };

  // Clipboard-Paste NUR wenn das Feld aktiv/fokussiert ist (Textfelder bleiben unberührt).
  const onPaste = (e) => {
    const items = e.clipboardData?.items || [];
    for (const it of items) {
      if (it.type?.startsWith("image/")) {
        const file = it.getAsFile();
        if (file) {
          e.preventDefault();
          processFile(file);
          return;
        }
      }
    }
  };

  const confirmReplace = () => {
    onChange(pending);
    setPending(null);
    toast.success("Bild ersetzt");
  };

  return (
    <div data-testid={testId}>
      <div className="text-[11px] uppercase tracking-wider text-zinc-400 mb-1.5">Foto / Trainingsgrafik</div>

      {value ? (
        <div className="space-y-2">
          <button type="button" onClick={() => setLightbox(true)}
            className={"block w-full rounded-lg overflow-hidden border border-zinc-800 bg-zinc-900 transition-colors group relative " +
              (accent === "emerald" ? "hover:border-emerald-400/50" : "hover:border-brand-lighter/50")}
            data-testid={`${testId}-thumb`}>
            <img src={value} alt="Trainingsgrafik" className="w-full h-auto max-h-[420px] object-contain bg-zinc-950" />
            <span className="absolute top-2 right-2 h-8 w-8 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
              <Maximize2 className="h-4 w-4" />
            </span>
          </button>
          <div className="flex gap-2">
            <button type="button" onClick={() => fileRef.current?.click()} disabled={busy}
              className={"inline-flex items-center gap-1.5 min-h-[38px] px-3 rounded-md border border-zinc-700 bg-zinc-950 text-zinc-200 text-xs font-semibold transition-colors " +
                (accent === "emerald" ? "hover:border-emerald-400/50 hover:text-emerald-300" : "hover:border-brand-lighter/50 hover:text-brand-lighter")}
              data-testid={`${testId}-replace`}>
              <Replace className="h-3.5 w-3.5" /> Bild ersetzen
            </button>
            <button type="button" onClick={() => onChange(null)}
              className="inline-flex items-center gap-1.5 min-h-[38px] px-3 rounded-md border border-zinc-700 bg-zinc-950 text-zinc-300 hover:border-rose-500/50 hover:text-rose-400 text-xs font-semibold transition"
              data-testid={`${testId}-delete`}>
              <Trash2 className="h-3.5 w-3.5" /> Löschen
            </button>
          </div>
        </div>
      ) : (
        <div
          tabIndex={0}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onClick={() => fileRef.current?.click()}
          onPaste={onPaste}
          onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
          onDrop={onDrop}
          className={
            "rounded-lg border-2 border-dashed p-6 text-center cursor-pointer transition outline-none " +
            (dragActive || focused
              ? (accent === "emerald" ? "border-emerald-500 bg-emerald-500/10 ring-2 ring-emerald-500/40" : "border-brand-lighter bg-brand/10 ring-2 ring-brand/40")
              : (accent === "emerald" ? "border-zinc-700 bg-zinc-900/40 hover:border-emerald-400/50" : "border-zinc-700 bg-zinc-900/40 hover:border-brand-lighter/50"))
          }
          data-testid={`${testId}-dropzone`}>
          <ImagePlus className="h-8 w-8 text-zinc-500 mx-auto mb-2" />
          <div className="text-sm text-zinc-200 font-medium">
            {busy ? "Bild wird optimiert …" : "Bild auswählen"}
          </div>
          <div className="text-xs text-zinc-500 mt-1">
            Hierher ziehen oder mit <span className="text-zinc-300">Strg+V / Cmd+V</span> einfügen
          </div>
          <div className="text-[10px] text-zinc-600 mt-1">
            {focused ? "Bereit zum Einfügen aus der Zwischenablage" : "Zum Einfügen zuerst hier klicken"}
          </div>
        </div>
      )}

      <input ref={fileRef} type="file" accept="image/*" hidden
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) processFile(f); }}
        data-testid={`${testId}-input`} />

      {/* Ersetzen-Bestätigung */}
      {pending && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" data-testid={`${testId}-replace-confirm`}>
          <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-5 max-w-sm w-full">
            <div className="text-sm font-semibold text-zinc-100 mb-2">Vorhandenes Bild ersetzen?</div>
            <p className="text-xs text-zinc-400 mb-4">Das aktuelle Bild wird durch das neue ersetzt.</p>
            <div className="flex gap-2">
              <button type="button" onClick={() => setPending(null)}
                className="flex-1 min-h-[38px] rounded-md border border-zinc-700 bg-transparent text-zinc-200 hover:bg-zinc-900 text-sm font-medium transition"
                data-testid={`${testId}-replace-cancel`}>Abbrechen</button>
              <button type="button" onClick={confirmReplace}
                className={"flex-1 min-h-[38px] rounded-md text-white text-sm font-semibold transition-colors " +
                  (accent === "emerald" ? "bg-emerald-600 hover:bg-emerald-500" : "bg-brand hover:bg-brand-light")}
                data-testid={`${testId}-replace-ok`}>Ersetzen</button>
            </div>
          </div>
        </div>
      )}

      {/* Vollbild */}
      {lightbox && (
        <div className="fixed inset-0 z-[70] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightbox(false)} data-testid={`${testId}-lightbox`}>
          <img src={value} alt="" className="max-h-full max-w-full object-contain" />
          <button onClick={() => setLightbox(false)}
            className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
            data-testid={`${testId}-lightbox-close`}
            aria-label="Vollbild schließen">
            <X className="h-5 w-5" />
          </button>
        </div>
      )}
    </div>
  );
}
